import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { useChatStore, type AvatarState } from '../state/chatStore'
import { Avatar } from './Avatar'
import { MessageBubble } from './MessageBubble'
import { PendingAction } from './PendingAction'
import { CameraView } from './CameraView'
import { useT, type UiStrings } from '../lib/i18n'
import type { VoiceSessionStatus } from '../hooks/useVoiceSession'
import { VoiceDiagnostics } from './VoiceDiagnostics'
import { debugGewuenscht } from '../lib/voiceDiagnostics'

/**
 * Der Sprachmodus als eigener Bildschirm — wie bei ChatGPT: K4ia groß in der
 * Mitte, darunter in einem Satz, was gerade passiert, unten zwei Knöpfe.
 * Gesprochen wird ohne Tippen; K4ia hört nach jeder Antwort von selbst wieder
 * zu (useVoiceSession, Modus `gespraech`).
 *
 * Unter K4ia steht die letzte Antwort als Sprechblase — mit allem, was dazu
 * gehört (Kontaktkarte, Bilder, „Ja, bitte“). Sonst wäre eine Rückfrage, die
 * eine Bestätigung braucht, im Sprachmodus nicht zu beantworten.
 *
 * Der ganze Verlauf steht zugleich im Chat: Wer „Beenden“ tippt, liest dort
 * nach, was gesagt wurde.
 */
export function VoiceModeOverlay({
  status,
  paused,
  errorMessage,
  onEnd,
  onPause,
  onResume,
  cameraOpen,
  onToggleCamera,
  bargeIn = false,
}: {
  status: VoiceSessionStatus
  paused: boolean
  errorMessage: string | null
  onEnd: () => void
  onPause: () => void
  /** Unterbrechen (während K4ia spricht) oder nach der Pause weitersprechen. */
  onResume: () => void
  cameraOpen: boolean
  onToggleCamera: (() => void) | null
  /** Unterbrechen per Stimme ist an — dann sagt der Satz das so. */
  bargeIn?: boolean
}) {
  const t = useT()
  const avatarState = useChatStore((s) => s.avatarState)
  const micLevel = useChatStore((s) => s.micLevel)
  const micOpen = useChatStore((s) => s.micOpen)
  const hoertDich = useChatStore((s) => s.hoertDich)
  const messages = useChatStore((s) => s.messages)
  const reduceMotion = useReducedMotion()
  const captionsRef = useRef<HTMLDivElement>(null)

  // Hintergrund steht still, solange der Sprachmodus offen ist; Escape beendet.
  const onEndRef = useRef(onEnd)
  onEndRef.current = onEnd
  useEffect(() => {
    const vorher = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onEndRef.current()
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = vorher
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  // Die letzten zwei Beiträge in ihrer Reihenfolge: meist die eigene Frage und ihre Antwort.
  const tail = messages.slice(-2)
  const last = tail[tail.length - 1]
  // Eine offene Karte (Kontaktkarte, Ja/Nein) hängt nicht an ihrer Antwort,
  // sondern steht angeheftet unter dem Verlauf — auch wenn der Besucher
  // inzwischen weitergeredet hat und die Antwort dazu aus dem Bild ist. Sie
  // bleibt, bis er sie abschickt oder schließt.
  const offeneKarte = messages.findLast((m) => m.pendingConfirmation)
  const karteId = offeneKarte?.pendingConfirmation?.toolCallId
  // Ist viel zu lesen (lange Antwort, Karte, Kamera), rückt K4ia zusammen:
  // Sonst war die Antwort oben abgeschnitten, während sie sprach.
  const kompakt =
    cameraOpen || Boolean(offeneKarte) || (last?.role === 'assistant' && last.content.length > 140)

  useEffect(() => {
    // Solange eine Karte offen ist, springt der Verlauf nicht mit — der
    // Besucher tippt vielleicht gerade hinein, und K4ia redet noch.
    if (karteId) return
    // `?.()`: jsdom (Tests) kennt scrollTo an Elementen nicht.
    captionsRef.current?.scrollTo?.({ top: captionsRef.current.scrollHeight, behavior: 'smooth' })
  }, [karteId, last?.id, last?.content])
  useEffect(() => {
    // Eine neue Karte: einmal hinunter, damit er sie sieht.
    if (!karteId) return
    captionsRef.current?.scrollTo?.({ top: captionsRef.current.scrollHeight, behavior: 'smooth' })
  }, [karteId])

  const phase = voicePhase(status, paused, avatarState)
  // Spricht der Besucher gerade? Mit etwas Nachlauf, damit „Ich höre dich“
  // nicht bei jeder Silbenpause zurückspringt.
  const sprichtGerade = useNachlauf(micLevel > HOER_SCHWELLE, 700)
  const hoert = (phase === 'listening' && sprichtGerade) || (phase === 'speaking' && hoertDich)
  // Nach dem eigenen Satz: Er ist angekommen — nicht bloß „einen Moment“.
  // (Ihre Sprechblase ist dann schon offen, aber noch leer — sie zählt nicht.)
  const letzteMitText = messages.findLast((m) => m.content.trim() !== '')
  const verstanden = phase === 'thinking' && letzteMitText?.role === 'user'
  // Ein Tipp auf K4ia: unterbrechen, während sie spricht; weiter nach der
  // Pause. Beim Zuhören tut er nichts — ein versehentlicher Tipp soll das
  // Gespräch nicht anhalten, dafür gibt es „Stumm“.
  const tapAction = phase === 'speaking' || phase === 'paused' ? onResume : null
  const tapLabel =
    phase === 'speaking'
      ? t.voiceModeInterrupt
      : phase === 'paused'
        ? t.voiceModeResume
        : phaseLabel(phase, t)
  const glow = GLOW[hoert ? 'listening' : phase]
  const satz = hoert
    ? t.voiceModeHearing
    : verstanden
      ? t.voiceModeUnderstood
      : phase === 'speaking' && bargeIn
        ? t.voiceModeSpeakingBargeIn
        : phaseLabel(phase, t)
  // Die Pegelwelle: beim Zuhören immer; während sie spricht nur, wenn das Gerät
  // auf Dazwischenreden hört — dann zeigt sie, dass es den Besucher trotzdem hört.
  const zeigeWelle = phase === 'listening' || (phase === 'speaking' && bargeIn)
  // Das Mikrofon ist auch an, während sie spricht und auf Dazwischenreden hört
  // (es sendet dann nichts, hört aber mit) — die Anzeige soll das nicht leugnen.
  const mikrofonAn = micOpen || (phase === 'speaking' && bargeIn)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t.voiceModeOpen}
      className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-bg"
    >
      {/* Ein Schein hinter K4ia, der mit dem Zustand atmet: stark beim Sprechen,
          ruhig beim Zuhören, fast weg in der Pause. Farbe ist nie das einzige
          Signal — der Satz unter ihr sagt es in Worten. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-[38%] flex h-0 items-center justify-center"
      >
        <motion.div
          className="h-[34rem] w-[34rem] shrink-0 rounded-full"
          style={{
            background: 'radial-gradient(closest-side, var(--color-accent), transparent 70%)',
          }}
          animate={{
            // Mit offener Kamera gibt es keine große K4ia in der Mitte — und
            // dann auch keinen Schein hinter dem Kamerabild.
            opacity: cameraOpen
              ? 0
              : glow.opacity + (phase === 'listening' || hoert ? micLevel * 0.35 : 0),
            scale: reduceMotion ? 1 : glow.scale,
          }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : {
                  opacity: { duration: 0.25 },
                  scale: { duration: glow.duration, repeat: Infinity, ease: 'easeInOut' },
                }
          }
        />
      </div>

      <header className="relative flex items-center justify-between gap-3 px-5 pb-2 pt-[max(env(safe-area-inset-top),1rem)]">
        <span
          className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${
            mikrofonAn ? 'glass text-text' : 'glass text-text-muted'
          }`}
        >
          <span
            aria-hidden
            className={`h-2 w-2 rounded-full ${mikrofonAn ? 'bg-status-active' : 'bg-border'}`}
          />
          {mikrofonAn ? t.voiceModeMicOn : t.voiceModeMicOff}
        </span>
        {onToggleCamera && (
          <button
            type="button"
            onClick={onToggleCamera}
            aria-pressed={cameraOpen}
            className={`flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold ${
              cameraOpen ? 'bg-status-active text-white' : 'glass text-text'
            }`}
          >
            <CameraIcon />
            {cameraOpen ? t.cameraOn : t.camera}
          </button>
        )}
      </header>

      {cameraOpen && onToggleCamera && (
        <div className="relative">
          <CameraView onClose={onToggleCamera} />
        </div>
      )}

      {cameraOpen ? (
        // Mit offener Kamera ist das Bild die Hauptsache: K4ia rückt in eine
        // schmale Zeile darunter — klein, mit dem Satz, was gerade passiert.
        // Vorher stand sie groß zwischen Kamera und Text und lag über dem
        // Eingabefeld der Kamera.
        <div
          className="relative flex items-center gap-3 px-6 py-2"
          data-testid="voice-kamera-zeile"
        >
          <button
            type="button"
            onClick={tapAction ?? undefined}
            disabled={!tapAction}
            aria-label={tapLabel}
            className="shrink-0 rounded-full outline-none focus-visible:ring-4 focus-visible:ring-brand disabled:cursor-default"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Avatar
              state={phase === 'paused' ? 'idle' : hoert ? 'listening' : avatarState}
              size={44}
              micLevel={micLevel}
              showLabel={false}
            />
          </button>
          <p
            className="flex min-w-0 flex-1 items-center gap-2 font-heading text-base text-text"
            aria-live="polite"
          >
            {verstanden && <HakenIcon klein />}
            <span className="truncate">{satz}</span>
          </p>
          {zeigeWelle && <HoerWelle level={micLevel} aktiv={hoert || phase === 'listening'} />}
          {status === 'error' && errorMessage && (
            <p role="alert" className="text-sm text-status-error">
              {errorMessage} — {t.voiceError}
            </p>
          )}
        </div>
      ) : (
        <div
          className={`relative flex min-h-0 flex-1 flex-col items-center justify-center px-6 ${
            kompakt ? 'gap-2' : 'gap-6'
          }`}
        >
          <motion.button
            type="button"
            layout
            transition={
              reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 260, damping: 28 }
            }
            onClick={tapAction ?? undefined}
            disabled={!tapAction}
            aria-label={tapLabel}
            className="rounded-full outline-none focus-visible:ring-4 focus-visible:ring-brand disabled:cursor-default"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Avatar
              // Hört sie den Besucher, während sie spricht, zeigt sie das sofort:
              // der Ring um sie geht mit seiner Stimme mit.
              state={phase === 'paused' ? 'idle' : hoert ? 'listening' : avatarState}
              size={kompakt ? 120 : 208}
              micLevel={micLevel}
              showLabel={false}
            />
          </motion.button>
          {/* Abstand nach oben: Der Ring um K4ia wächst mit der Stimme um bis zu
            ein Viertel ihrer Größe — die Balken sollen nicht darunter liegen. */}
          <div className={`flex h-8 items-center justify-center ${kompakt ? 'mt-2' : 'mt-6'}`}>
            {zeigeWelle && <HoerWelle level={micLevel} aktiv={hoert || phase === 'listening'} />}
          </div>
          <p
            className="-mt-3 flex min-h-[1.75rem] items-center gap-2 text-center font-heading text-xl text-text"
            aria-live="polite"
          >
            {verstanden && <HakenIcon />}
            {satz}
          </p>
          {debugGewuenscht() && (
            <div className="w-full max-w-xl">
              <VoiceDiagnostics status={status} />
            </div>
          )}
          {status === 'error' && errorMessage && (
            <p role="alert" className="max-w-sm text-center text-sm text-status-error">
              {errorMessage} — {t.voiceError}
            </p>
          )}
        </div>
      )}

      {/* Was zuletzt gesagt wurde — die eigene Frage und K4ias Antwort; darunter
          angeheftet, was der Besucher noch entscheiden muss. */}
      <div
        ref={captionsRef}
        className={`relative mx-auto w-full max-w-xl space-y-3 overflow-y-auto overscroll-contain px-4 pb-2 ${
          cameraOpen ? 'min-h-0 flex-1' : kompakt ? 'max-h-[58vh]' : 'max-h-[34vh]'
        }`}
      >
        {tail.map((m) =>
          m.role === 'assistant' && m.streaming && !m.content ? (
            // Sie spricht schon, der Text ist noch unterwegs: ElevenLabs
            // schickt ihn erst, wenn der Satz fertig ist. Drei Punkte statt
            // einer leeren Blase.
            <SprichtPunkte
              key={m.id}
              label={phase === 'speaking' ? t.stateSpeaking : t.stateThinking}
            />
          ) : m.role === 'user' ? (
            // Die eigene Frage, wie K4ia sie verstanden hat — sanft eingeblendet,
            // damit man sie bemerkt: Das ist der Beweis, dass sie angekommen ist.
            <motion.p
              key={m.id}
              initial={reduceMotion ? false : { opacity: 0, y: 8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="ml-auto flex w-fit max-w-[85%] items-start gap-2 rounded-[1.375rem] rounded-br-md bg-surface-2 px-4 py-2.5 text-sm text-text"
            >
              <span>{m.content}</span>
              <HakenIcon klein />
            </motion.p>
          ) : (
            <MessageBubble key={m.id} message={m} ohneAktion />
          ),
        )}
        {offeneKarte && (
          <div
            className="rounded-[1.375rem] bg-surface px-4 pb-3 pt-1 shadow-[var(--shadow-card)]"
            data-testid="voice-pending"
          >
            <p className="text-xs text-text-muted">{t.voiceModeCardOpen}</p>
            <PendingAction message={offeneKarte} />
          </div>
        )}
      </div>

      <div className="relative flex items-start justify-center gap-10 px-6 pb-[max(env(safe-area-inset-bottom),1.25rem)] pt-4">
        {status === 'error' ? (
          <RoundButton label={t.voiceModeBack} onClick={onEnd} tone="neutral">
            <ChatIcon />
          </RoundButton>
        ) : (
          <RoundButton
            label={paused ? t.voiceUnmute : t.voiceMute}
            onClick={paused ? onResume : onPause}
            pressed={paused}
            tone={paused ? 'active' : 'neutral'}
            disabled={status !== 'active'}
          >
            {paused ? <MicOffIcon /> : <MicIcon />}
          </RoundButton>
        )}
        <RoundButton label={t.voiceModeEnd} onClick={onEnd} tone="danger">
          <CloseIcon />
        </RoundButton>
      </div>
    </div>
  )
}

/**
 * Drei Punkte, die nacheinander aufleuchten — der Platzhalter, solange K4ia
 * schon spricht, der Text aber noch nicht da ist.
 */
function SprichtPunkte({ label }: { label: string }) {
  const reduceMotion = useReducedMotion()
  return (
    <div
      role="status"
      aria-label={label}
      className="flex w-fit items-center gap-1.5 rounded-[1.375rem] rounded-bl-md bg-surface px-4 py-3 shadow-[var(--shadow-card)]"
      data-testid="spricht-punkte"
    >
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          aria-hidden
          className="block h-2 w-2 rounded-full bg-text-muted"
          animate={reduceMotion ? { opacity: 0.6 } : { opacity: [0.25, 1, 0.25] }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : { duration: 1.2, repeat: Infinity, delay: i * 0.2, ease: 'easeInOut' }
          }
        />
      ))}
    </div>
  )
}

/** Ab diesem Pegel (0…1, wie micLevel) zählt es als „der Besucher spricht“. */
const HOER_SCHWELLE = 0.08

/** `true`, solange `wert` wahr ist — und noch `ms` danach. */
function useNachlauf(wert: boolean, ms: number): boolean {
  const [an, setAn] = useState(wert)
  useEffect(() => {
    if (wert) {
      setAn(true)
      return
    }
    const timer = setTimeout(() => setAn(false), ms)
    return () => clearTimeout(timer)
  }, [wert, ms])
  return wert || an
}

/**
 * Fünf Balken, die mit der Stimme des Besuchers mitgehen — wie bei ChatGPT
 * oder Siri. Sie antworten auf die Frage, die man sich vor einem Gerät sonst
 * stellt: Hört es mich gerade? `aktiv` färbt sie; ruhig stehen sie als
 * Punkte da, damit man sieht, dass das Gerät bereit ist.
 */
function HoerWelle({ level, aktiv }: { level: number; aktiv: boolean }) {
  const reduceMotion = useReducedMotion()
  return (
    <div aria-hidden className="flex h-8 items-center gap-1.5" data-testid="hoerwelle">
      {WELLE.map((anteil, i) => (
        <motion.span
          key={i}
          className={`block w-1.5 rounded-full ${aktiv ? 'bg-accent' : 'bg-border'}`}
          animate={{ height: 6 + Math.min(1, level * 1.6) * 26 * anteil }}
          transition={{ duration: reduceMotion ? 0 : 0.1, ease: 'easeOut' }}
        />
      ))}
    </div>
  )
}

/** Mitte am höchsten, außen flacher. */
const WELLE = [0.45, 0.75, 1, 0.75, 0.45]

function HakenIcon({ klein = false }: { klein?: boolean }) {
  const px = klein ? 16 : 20
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={`shrink-0 text-status-active ${klein ? 'mt-0.5' : ''}`}
    >
      <path
        d="M5 12.5l4.5 4.5L19 7.5"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export type VoicePhase = 'connecting' | 'listening' | 'thinking' | 'speaking' | 'paused' | 'error'

/** Ein Wort für den Moment — daraus folgen Satz, Schein und was ein Tipp auf K4ia tut. */
export function voicePhase(
  status: VoiceSessionStatus,
  paused: boolean,
  avatarState: AvatarState,
): VoicePhase {
  if (status === 'error') return 'error'
  if (status !== 'active') return 'connecting'
  if (avatarState === 'speaking') return 'speaking'
  if (paused) return 'paused'
  if (avatarState === 'thinking') return 'thinking'
  return 'listening'
}

function phaseLabel(phase: VoicePhase, t: UiStrings): string {
  switch (phase) {
    case 'connecting':
      return t.voiceModeConnecting
    case 'listening':
      return t.voiceModeListening
    case 'thinking':
      return t.voiceModeThinking
    case 'speaking':
      return t.voiceModeSpeaking
    case 'paused':
      return t.voiceModePaused
    case 'error':
      return t.stateError
  }
}

const GLOW: Record<VoicePhase, { opacity: number; scale: number[]; duration: number }> = {
  connecting: { opacity: 0.12, scale: [0.95, 1.02, 0.95], duration: 2.4 },
  listening: { opacity: 0.22, scale: [1, 1.03, 1], duration: 3 },
  thinking: { opacity: 0.18, scale: [0.94, 1.04, 0.94], duration: 1.4 },
  speaking: { opacity: 0.38, scale: [0.96, 1.08, 0.96], duration: 0.8 },
  paused: { opacity: 0.06, scale: [1, 1, 1], duration: 1 },
  error: { opacity: 0.05, scale: [1, 1, 1], duration: 1 },
}

function RoundButton({
  label,
  onClick,
  tone,
  pressed,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  tone: 'neutral' | 'active' | 'danger'
  pressed?: boolean
  disabled?: boolean
  children: React.ReactNode
}) {
  const farbe =
    tone === 'danger'
      ? 'bg-status-error text-white'
      : tone === 'active'
        ? 'bg-text text-bg'
        : 'glass text-text'
  return (
    <div className="flex w-20 flex-col items-center gap-2">
      <button
        type="button"
        onClick={onClick}
        aria-pressed={pressed}
        aria-label={label}
        disabled={disabled}
        className={`flex h-16 w-16 items-center justify-center rounded-full shadow-float transition disabled:opacity-40 ${farbe}`}
      >
        {children}
      </button>
      <span aria-hidden className="text-xs font-medium text-text-muted">
        {label}
      </span>
    </div>
  )
}

function MicIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M19 11a7 7 0 0 1-14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function MicOffIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M15 10V6a3 3 0 0 0-5.7-1.3M9 9v3a3 3 0 0 0 4.6 2.5M19 11a7 7 0 0 1-1.2 3.9M5 11a7 7 0 0 0 10.4 6.1M12 18v3M4 4l16 16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6 6 18"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 5h16v11H9l-5 4V5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  )
}

function CameraIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 8h3l2-3h6l2 3h3v11H4V8Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13" r="3.5" stroke="currentColor" strokeWidth="2" />
    </svg>
  )
}
